// InfoTile.js
// -----------------------------------------------------------------------------
// Compact icon + label + value tile. Designed for contact / footer details
// (phone, address, email, service times). Optionally links the value.
//
// Props:
//   - icon     : node     (default undef)   leading icon
//   - label    : node     (required)        small label (e.g. "Email")
//   - value    : node     (required)        the value (e.g. "hi@nhv.com")
//   - href     : string   (default undef)   makes value a link
//                supports "mailto:" / "tel:" / "https://" - external opens tab
//   - inverted : boolean  (default false)   light text for dark backgrounds
//   - sx       : object   (default {})       extra Joy sx overrides
// -----------------------------------------------------------------------------

import * as React from 'react';
import PropTypes from 'prop-types';
import Stack from '@mui/joy/Stack';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Link from '@mui/joy/Link';

const InfoTile = ({ icon, label, value, href, inverted = false, sx = {} }) => {
  // Zero-trust: guard required props.
  if (label === undefined || label === null || value === undefined || value === null) {
    return null;
  }

  const labelColor = inverted ? 'common.white' : 'text.tertiary';
  // Inverted labels soften the white token via opacity rather than raw rgba().
  const labelOpacity = inverted ? 0.7 : 1;
  const valueColor = inverted ? 'common.white' : 'text.primary';
  const iconColor = inverted ? 'accent.300' : 'primary.500';

  const safeHref = typeof href === 'string' && href.length > 0 ? href : null;
  // Only mailto:/tel: stay in-page; http(s) opens a new tab safely.
  const isExternal = safeHref ? /^https?:\/\//i.test(safeHref) : false;

  const valueNode = safeHref ? (
    <Link
      href={safeHref}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      sx={{ color: valueColor, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
    >
      {value}
    </Link>
  ) : (
    <Typography level="body-md" sx={{ color: valueColor }}>
      {value}
    </Typography>
  );

  return (
    <Stack direction="row" spacing={1.75} sx={{ alignItems: 'flex-start', ...sx }}>
      {icon ? (
        <Box sx={{ color: iconColor, display: 'inline-flex', flexShrink: 0, mt: '2px' }}>
          {icon}
        </Box>
      ) : null}
      <Box>
        <Typography
          level="body-xs"
          sx={{ color: labelColor, opacity: labelOpacity, textTransform: 'uppercase', letterSpacing: '0.06em' }}
        >
          {label}
        </Typography>
        <Box sx={{ mt: 0.25 }}>{valueNode}</Box>
      </Box>
    </Stack>
  );
};

InfoTile.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
  href: PropTypes.string,
  inverted: PropTypes.bool,
  sx: PropTypes.object,
};

export default InfoTile;
